import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, Trash2, Loader2, Mail, Copy, Check, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Team() {
  const [team, setTeam] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInviting, setIsInviting] = useState(false);
  const [name, setName] = useState('');
  const [emailLocal, setEmailLocal] = useState('');
  const [lastInviteUrl, setLastInviteUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const res = await axios.get(`${API}/signatures/team`);
      setTeam(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load your team');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!team) return;
    setIsInviting(true);
    setLastInviteUrl(null);
    try {
      const res = await axios.post(`${API}/signatures/team/invite`, {
        name,
        email: `${emailLocal}@${team.domain}`,
      });
      if (res.data.email_sent) {
        toast.success('Invite sent');
      } else {
        toast.success('Teammate added - share this invite link with them (email is not configured)');
        setLastInviteUrl(res.data.invite_url);
      }
      setName('');
      setEmailLocal('');
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemove = async (teammateId) => {
    if (!window.confirm('Remove this teammate? They will no longer be able to sign in.')) return;
    try {
      await axios.delete(`${API}/signatures/team/${teammateId}`);
      toast.success('Teammate removed');
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove teammate');
    }
  };

  const copyInviteLink = async () => {
    if (!lastInviteUrl) return;
    try {
      await navigator.clipboard.writeText(lastInviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Copy failed');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-black" />
      </div>
    );
  }

  if (!team) return null;

  const seatsRemaining = Math.max(0, team.seats - team.used);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-xl font-semibold">Team</h2>
        <p className="text-slate-500 mt-1">
          Invite teammates on <span className="font-mono">@{team.domain}</span> to build their own
          email signature. {team.used} of {team.seats} seats used.
        </p>
      </div>

      {seatsRemaining > 0 && (
        <form onSubmit={handleInvite} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-4">
          <h3 className="font-semibold text-sm">Invite a teammate</h3>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="Their name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="flex-1 bg-gray-50 border-0 rounded-xl py-2.5 px-4 text-sm focus:ring-2 focus:ring-black outline-none"
            />
            <div className="flex-1 flex bg-gray-50 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-black">
              <input
                type="text"
                placeholder="name"
                value={emailLocal}
                onChange={(e) => setEmailLocal(e.target.value.replace(/@.*$/, ''))}
                required
                className="flex-1 bg-transparent border-0 py-2.5 pl-4 text-sm outline-none min-w-0"
              />
              <span className="flex items-center pr-4 text-sm text-gray-400 whitespace-nowrap">@{team.domain}</span>
            </div>
            <button
              type="submit"
              disabled={isInviting}
              className="bg-black text-white px-5 py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:scale-[1.02] transition-all disabled:opacity-50"
            >
              {isInviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Invite
            </button>
          </div>

          {lastInviteUrl && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center justify-between gap-3">
              <span className="text-xs text-amber-800 font-mono truncate">{lastInviteUrl}</span>
              <button
                type="button"
                onClick={copyInviteLink}
                className="shrink-0 bg-white border border-amber-300 text-amber-800 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copied' : 'Copy link'}
              </button>
            </div>
          )}
        </form>
      )}

      {seatsRemaining === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 text-sm text-gray-600">
          No seats remaining. Contact us to add more.
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {team.teammates.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">No teammates yet.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {team.teammates.map((member) => (
              <div key={member.id} className="flex items-center justify-between gap-4 p-4">
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{member.name || member.email}</p>
                  <p className="text-xs text-gray-500 flex items-center gap-1.5 truncate">
                    <Mail className="w-3 h-3 shrink-0" />
                    {member.email}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {member.is_active ? (
                    <span className="text-xs font-semibold text-green-700 bg-green-100 px-2.5 py-1 rounded-full">Active</span>
                  ) : (
                    <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Pending
                    </span>
                  )}
                  <button
                    onClick={() => handleRemove(member.id)}
                    className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-500 hover:text-white transition-all"
                    title="Remove teammate"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
